"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { Activity, AlertTriangle, BarChart3, Building2, Cpu, Gauge, GitMerge, KeyRound, LayoutDashboard, LogOut, Mail, Menu, MessageCircle, MessageSquare, Percent, Route, Server, Settings, ShieldAlert, Sparkles } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";
import { Logo } from "./ui/logo";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /**
   * "prefix" highlights the entry on its detail routes too; "exact" keeps the
   * highlight on the index page only.
   */
  match: "exact" | "prefix";
}
const navItems: NavItem[] = [{
  href: "/",
  label: "Dashboard",
  icon: LayoutDashboard,
  match: "exact"
}, {
  href: "/organizations",
  label: "Organizations",
  icon: Building2,
  match: "prefix"
}, {
  href: "/devpass",
  label: "DevPass",
  icon: Sparkles,
  match: "prefix"
}, {
  href: "/chat-plans",
  label: "Lounge Plans",
  icon: MessageSquare,
  match: "prefix"
}, {
  href: "/global-stats",
  label: "Global Stats",
  icon: BarChart3,
  match: "prefix"
}, {
  href: "/discounts",
  label: "Discounts",
  icon: Percent,
  match: "exact"
}, {
  href: "/rate-limits",
  label: "Global Rate Limits",
  icon: Gauge,
  match: "exact"
}, {
  href: "/providers",
  label: "Providers",
  icon: Server,
  match: "exact"
}, {
  href: "/provider-credentials",
  label: "Provider Credentials",
  icon: KeyRound,
  match: "prefix"
}, {
  href: "/models",
  label: "Models",
  icon: Cpu,
  match: "exact"
}, {
  href: "/model-provider-mappings",
  label: "Model Mappings",
  icon: GitMerge,
  match: "exact"
}, {
  href: "/routing-analytics",
  label: "Routing Analytics",
  icon: Route,
  match: "prefix"
}, {
  href: "/unstable-mappings",
  label: "Unstable Mappings",
  icon: Activity,
  match: "prefix"
}, {
  href: "/contact-submissions",
  label: "Contact Submissions",
  icon: Mail,
  match: "prefix"
}, {
  href: "/provider-listing-requests",
  label: "Provider Requests",
  icon: Building2,
  match: "prefix"
}, {
  href: "/chat-support-logs",
  label: "Chat Support Logs",
  icon: MessageCircle,
  match: "prefix"
}, {
  href: "/payment-failures",
  label: "Payment Failures",
  icon: AlertTriangle,
  match: "prefix"
}, {
  href: "/flagged-accounts",
  label: "Flagged Accounts",
  icon: ShieldAlert,
  match: "prefix"
}, {
  href: "/limit-hits",
  label: "Limit Hits",
  icon: ShieldAlert,
  match: "prefix"
}, {
  href: "/settings",
  label: "Settings",
  icon: Settings,
  match: "exact"
}];
function isActive(item: NavItem, pathname: string): boolean {
  if (item.href === "/") {
    return pathname === "/" || pathname === "";
  }
  return item.match === "exact" ? pathname === item.href : pathname.startsWith(item.href);
}
interface AdminShellProps {
  children: ReactNode;
  /**
   * Server-rendered session-cookie presence. Used only as the fallback while
   * `/user/me` is still in flight, so the navigation does not flash in or out
   * on every full page load.
   */
  signedIn: boolean;
}
function MobileHeader() {
  const $ = _c(9);
  const {
    toggleSidebar
  } = useSidebar();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Menu className="h-5 w-5" />;
    t1 = <span className="sr-only">Toggle menu</span>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== toggleSidebar) {
    t2 = <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleSidebar}>{t0}{t1}</Button>;
    $[2] = toggleSidebar;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-2">{t3}<div className="flex flex-col"><span className="text-sm font-semibold leading-tight">LLM Gateway</span><span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div></div>;
    t5 = <ThemeToggle size="compact" className="ml-auto" />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2) {
    t6 = <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border/60 bg-background px-4 md:hidden">{t2}{t4}{t5}</header>;
    $[7] = t2;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
export function AdminShell(t0) {
  const $ = _c(31);
  const {
    children,
    signedIn
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    signOut
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    user,
    isLoading,
    error,
    data
  } = useUser();
  const showNav = user ? user.isAdmin : isLoading && !error && signedIn;
  if (!showNav) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="absolute right-4 top-4 z-50"><ThemeToggle size="compact" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== children) {
      t2 = <div className="relative min-h-svh w-full">{t1}{children}</div>;
      $[1] = children;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== queryClient || $[4] !== router || $[5] !== signOut) {
    t1 = async () => {
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push("/login");
          }
        }
      });
    };
    $[3] = queryClient;
    $[4] = router;
    $[5] = signOut;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleSignOut = t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SidebarHeader className="border-b border-sidebar-border/60"><div className="flex h-12 items-center justify-between px-2"><div className="flex items-center gap-2 px-1">{t2}<div className="flex flex-col"><span className="text-sm font-semibold leading-tight">LLM Gateway</span><span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div></div><SidebarTrigger className="hidden md:flex" /></div></SidebarHeader>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SidebarGroupLabel>Main</SidebarGroupLabel>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== pathname) {
    t5 = navItems.map(item => {
      const Icon = item.icon;
      return <SidebarMenuItem key={item.href}><Link href={item.href} prefetch={true} className="block"><SidebarMenuButton isActive={isActive(item, pathname)} size="lg"><Icon className="h-4 w-4" /><span>{item.label}</span></SidebarMenuButton></Link></SidebarMenuItem>;
    });
    $[10] = pathname;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <SidebarContent><SidebarGroup>{t4}<SidebarMenu>{t5}</SidebarMenu></SidebarGroup></SidebarContent>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex justify-center"><ThemeToggle size="compact" /></div>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <LogOut className="h-3.5 w-3.5" />;
    t9 = <span>Sign out</span>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== handleSignOut) {
    t10 = <SidebarFooter className="border-t border-sidebar-border/60">{t7}<Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-xs text-muted-foreground" onClick={handleSignOut}>{t8}{t9}</Button></SidebarFooter>;
    $[17] = handleSignOut;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6) {
    t11 = <Sidebar variant="inset">{t3}{t6}{t10}</Sidebar>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <MobileHeader />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== data?.enterpriseLicense?.status) {
    t13 = data?.enterpriseLicense?.status === "grace" && <Alert className="rounded-none border-x-0 border-t-0 border-amber-500/40 bg-amber-500/10 px-6 text-amber-950 dark:text-amber-100"><AlertTriangle /><AlertTitle>Enterprise license expired</AlertTitle><AlertDescription>Admin access remains available during the seven-day grace period. Install a renewed license before it ends.</AlertDescription></Alert>;
    $[23] = data?.enterpriseLicense?.status;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== children || $[26] !== t13) {
    t14 = <SidebarInset>{t12}{t13}{children}</SidebarInset>;
    $[25] = children;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t14) {
    t15 = <SidebarProvider>{t11}{t14}</SidebarProvider>;
    $[28] = t11;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}